import { useState, useRef, useEffect } from 'react';
import { Download, FileText, Lock, Loader2, Map, MessageSquare, Volume2, Layers } from 'lucide-react';
import { hasFeature } from '@shared/services/featureGating';
import { exportLearningPath, type ExportFormat } from '@shared/services/learningPath';
import type { SubscriptionTier } from '@shared/constants/tiers';

interface ExportOption {
  format: ExportFormat;
  label: string;
  icon: typeof FileText;
  /** Feature flag the tier must hold to use this export. */
  requires: 'pdf_export_map' | 'exports_all';
}

const OPTIONS: ExportOption[] = [
  { format: 'pdf_map', label: 'Concept map (PDF)', icon: Map, requires: 'pdf_export_map' },
  { format: 'cheat_sheet', label: 'Cheat sheet (PDF)', icon: FileText, requires: 'exports_all' },
  { format: 'flashcards', label: 'Anki deck (.apkg)', icon: Layers, requires: 'exports_all' },
  { format: 'audio', label: 'Audio summary (MP3)', icon: Volume2, requires: 'exports_all' },
  { format: 'tutor_log', label: 'Tutor log (PDF)', icon: MessageSquare, requires: 'exports_all' },
];

function downloadBase64(base64: string, filename: string, mime: string): void {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ExportMenu({
  pathId,
  tier,
  onLockedClick,
}: {
  pathId: string;
  tier: SubscriptionTier;
  onLockedClick: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  async function handleExport(opt: ExportOption) {
    if (!hasFeature(tier, opt.requires)) {
      setOpen(false);
      onLockedClick();
      return;
    }
    setBusy(opt.format);
    setError(null);
    try {
      const res = await exportLearningPath(pathId, opt.format);
      downloadBase64(res.base64, res.filename, res.mimeType);
      setOpen(false);
    } catch {
      setError('Export failed. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-dark-surface border border-dark-border text-sm text-content-secondary hover:text-content hover:border-indigo-400 transition-colors"
      >
        <Download size={15} />
        Export
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-xl bg-dark-surface border border-dark-border shadow-xl z-40 p-1.5">
          {OPTIONS.map((opt) => {
            const unlocked = hasFeature(tier, opt.requires);
            const Icon = opt.icon;
            return (
              <button
                key={opt.format}
                onClick={() => handleExport(opt)}
                disabled={busy !== null}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left text-content-secondary hover:bg-dark-bg transition-colors disabled:opacity-50"
              >
                {busy === opt.format ? (
                  <Loader2 size={15} className="animate-spin shrink-0" />
                ) : (
                  <Icon size={15} className="shrink-0 text-content-muted" />
                )}
                <span className="flex-1">{opt.label}</span>
                {!unlocked && <Lock size={13} className="text-gold-400 shrink-0" />}
              </button>
            );
          })}
          {error && <p className="text-xs text-error px-3 py-1.5">{error}</p>}
          {!hasFeature(tier, 'exports_all') && (
            <p className="text-[11px] text-content-dim px-3 py-1.5 border-t border-dark-border mt-1">
              {hasFeature(tier, 'pdf_export_map')
                ? 'Upgrade to Pro for all export formats.'
                : 'Exports are available on paid plans.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
